import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Select,
  MenuItem,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import Layout from '../../components/Layout';
import { useAppContext } from '../../context/AppContext';
import ApiService, { ICountryData } from '../../services/api';
import { logo } from '../../assets';
import { COMMON_MUI_STYLES } from '../../styles';

// Using shared common styles from styles.ts

// Country phone number length mapping (min and max digits)
const COUNTRY_PHONE_LENGTHS: { [key: string]: { min: number; max: number } } = {
  "United States": { min: 10, max: 10 },
  "Canada": { min: 10, max: 10 },
  "United Kingdom": { min: 9, max: 10 },
  "Australia": { min: 9, max: 9 },
  "Germany": { min: 10, max: 11 },
  "France": { min: 9, max: 9 },
  "Italy": { min: 9, max: 10 },
  "Japan": { min: 10, max: 10 },
  "China": { min: 11, max: 11 },
  "Brazil": { min: 10, max: 11 },
  "Russia": { min: 10, max: 10 },
  "Mexico": { min: 10, max: 10 },
  "Spain": { min: 9, max: 9 },
  "South Korea": { min: 10, max: 11 },
  "United Arab Emirates": { min: 9, max: 9 },
  "Saudi Arabia": { min: 9, max: 9 },
  "Singapore": { min: 8, max: 8 },
  "South Africa": { min: 9, max: 9 },
  "New Zealand": { min: 8, max: 10 },
  "Albania": { min: 8, max: 9 },
  "Austria": { min: 10, max: 13 },
  "Belgium": { min: 8, max: 10 },
  "Bosnia-Herzegovina": { min: 8, max: 9 },
  "Botswana": { min: 7, max: 8 },
  "Bulgaria": { min: 8, max: 9 },
  "Croatia": { min: 8, max: 9 },
  "Cyprus": { min: 8, max: 8 },
  "Czech Republic": { min: 9, max: 9 },
  "Denmark": { min: 8, max: 8 },
  "Estonia": { min: 7, max: 8 },
  "Finland": { min: 5, max: 12 },
  "French Polynesia": { min: 6, max: 8 },
  "Ghana": { min: 9, max: 9 },
  "Greece": { min: 10, max: 10 },
  "Hungary": { min: 8, max: 9 },
  "Iceland": { min: 7, max: 9 },
  "Ireland": { min: 7, max: 9 },
  "Israel": { min: 8, max: 9 },
  "Kosovo": { min: 8, max: 8 },
  "Latvia": { min: 8, max: 8 },
  "Lesotho": { min: 8, max: 8 },
  "Lithuania": { min: 8, max: 8 },
  "Macedonia": { min: 8, max: 8 },
  "Malta": { min: 8, max: 8 },
  "Moldova": { min: 8, max: 8 },
  "Montenegro": { min: 8, max: 8 },
  "Namibia": { min: 9, max: 9 },
  "Netherlands": { min: 9, max: 9 },
  "North Macedonia": { min: 8, max: 8 },
  "Norway": { min: 8, max: 8 },
  "Poland": { min: 9, max: 9 },
  "Portugal": { min: 9, max: 9 },
  "Romania": { min: 9, max: 9 },
  "Serbia": { min: 8, max: 9 },
  "Slovak Republic": { min: 9, max: 9 },
  "Slovakia": { min: 9, max: 9 },
  "Slovenia": { min: 8, max: 8 },
  "Swaziland": { min: 8, max: 8 },
  "Eswatini": { min: 8, max: 8 },
  "Sweden": { min: 7, max: 13 },
  "Switzerland": { min: 9, max: 9 },
  "Turkey": { min: 10, max: 10 },
  "Ukraine": { min: 9, max: 9 },
};

// Map dial codes to correct ISO flag codes
// This is more reliable than country_code since many countries have country_code="GB" for English language
// but need their correct flag displayed based on dial_code
const getFlagCodeFromDialCode = (dialCode: string | undefined): string | null => {
  if (!dialCode) return null;
  
  // Map dial codes to correct ISO 3166-1 alpha-2 codes for flags
  const dialCodeToFlagCodeMap: Record<string, string> = {
    '355': 'AL', // Albania
    '43': 'AT',  // Austria
    '32': 'BE',  // Belgium
    '387': 'BA', // Bosnia-Herzegovina
    '267': 'BW', // Botswana
    '359': 'BG', // Bulgaria
    '385': 'HR', // Croatia
    '357': 'CY', // Cyprus
    '420': 'CZ', // Czech Republic
    '45': 'DK',  // Denmark
    '372': 'EE', // Estonia
    '358': 'FI', // Finland
    '33': 'FR',  // France
    '689': 'PF', // French Polynesia
    '49': 'DE',  // Germany
    '233': 'GH', // Ghana
    '30': 'GR',  // Greece
    '36': 'HU',  // Hungary
    '354': 'IS', // Iceland
    '353': 'IE', // Ireland
    '972': 'IL', // Israel
    '39': 'IT',  // Italy
    '383': 'XK', // Kosovo
    '82': 'KR',  // Korea
    '371': 'LV', // Latvia
    '266': 'LS', // Lesotho
    '370': 'LT', // Lithuania
    '389': 'MK', // Macedonia / North Macedonia
    '356': 'MT', // Malta
    '373': 'MD', // Moldova
    '382': 'ME', // Montenegro
    '264': 'NA', // Namibia
    '31': 'NL',  // Netherlands
    '47': 'NO',  // Norway
    '48': 'PL',  // Poland
    '351': 'PT', // Portugal
    '40': 'RO',  // Romania
    '381': 'RS', // Serbia
    '421': 'SK', // Slovakia / Slovak Republic
    '386': 'SI', // Slovenia
    '27': 'ZA',  // South Africa
    '34': 'ES',  // Spain
    '268': 'SZ', // Swaziland / Eswatini
    '46': 'SE',  // Sweden
    '41': 'CH',  // Switzerland
    '90': 'TR',  // Turkey
    '380': 'UA', // Ukraine
    '44': 'GB',  // United Kingdom
  };
  
  return dialCodeToFlagCodeMap[dialCode] || null;
};

const EditProfilePage: React.FC = () => {
  const {
    userId,
    token,
    sessionId,
    getTranslation,
    setSnackbar,
    role,
    fetchUserProfiles,
    userProfilesCache,
  } = useAppContext();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryId, setCountryId] = useState('');
  const [countries, setCountries] = useState<ICountryData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [flagsLoading, setFlagsLoading] = useState<{ [key: string]: boolean }>({});
  const [dataPopulated, setDataPopulated] = useState(false);
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    country_id?: string;
  }>({});

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const api = new ApiService();
        const countryDataResponse = await api.getCountryData();
        setCountries(countryDataResponse.languageData || []);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
        setCountries([]);
      }
    };
    fetchCountries();
  }, []);

  // Extract user object from different response structures
  const extractUserFromResponse = (response: any): any => {
    if (!response) return null;

    // Handle array format
    if (Array.isArray(response) && response.length > 0) {
      const item = response[0];
      return item?.user?.user || item?.user || item;
    }

    // Handle object format
    if (typeof response === 'object') {
      // Nested structure: { user: { user: {...} } }
      if (response.user?.user) return response.user.user;
      if (response.user) return response.user;
      // Direct user object
      if (response.first_name || response.last_name) return response;
    }

    return null;
  };

  // Step 2: User data loads and populates country
  // Populate form fields from user data
  const populateFieldsFromUser = (user: any) => {
    if (!user) return;

    setFirstName(user.first_name || '');
    setLastName(user.last_name || '');
    setPhoneNumber(user.phone_number || '');

    // Match country accurately
    let matchedCountry: ICountryData | undefined;
    
    // First try to match by country_data_id
    if (user.country_data_id && countries.length > 0) {
      matchedCountry = countries.find(c => c._id === user.country_data_id);
    }
    
    // Fallback to dial_code matching (more reliable than country_code since many have country_code="GB")
    // First check if user has dial_code directly - convert both to strings for reliable comparison
    if (!matchedCountry && user.dial_code && countries.length > 0) {
      const userDialCode = String(user.dial_code).trim();
      matchedCountry = countries.find(
        (c) => c.dial_code && String(c.dial_code).trim() === userDialCode
      );
    }
    
    // Final fallback to country_code if needed
    if (!matchedCountry && user.country_code && countries.length > 0) {
      matchedCountry = countries.find(
        (c) => c.country_code?.toUpperCase() === user.country_code.toUpperCase()
      );
    }
    
    if (matchedCountry?._id) {
      setCountryId(matchedCountry._id);
    }
  };

  // Fetch user profile data and populate form fields
  useEffect(() => {
    if (dataPopulated || !countries.length) return;

    // Fetch fresh data if we have credentials and not already loading
    if (!userId || !token || userProfilesCache.isLoading) return;

    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const api = new ApiService(sessionId, token);
        const response = await fetchUserProfiles(api, role || undefined);
        const user = extractUserFromResponse(response);
        
        if (user) {
          populateFieldsFromUser(user);
          setDataPopulated(true);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [countries.length, userProfilesCache.isLoading, userId, token, sessionId, role, dataPopulated, fetchUserProfiles]);

  // Phone number validation function
  const validatePhoneNumber = (value: string, countryId?: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) {
      return getTranslation("Txt949");
    }
    const normalized = trimmed.replace(/\D/g, "");
    
    // Get country name from countryId
    let countryName: string | undefined;
    if (countryId && countries.length > 0) {
      const selectedCountry = countries.find(c => c._id === countryId);
      countryName = selectedCountry?.country_name;
    }
    
    // If country is selected, validate against country-specific length
    if (countryName && COUNTRY_PHONE_LENGTHS[countryName]) {
      const { min, max } = COUNTRY_PHONE_LENGTHS[countryName];
      if (normalized.length < min || normalized.length > max) {
        return `${getTranslation("Txt950")} ${min}${min !== max ? `-${max}` : ''} ${getTranslation("Txt951")}`;
      }
    } else {
      // Default validation: 10 digits for any other country
      if (normalized.length !== 10) {
        return `${getTranslation("Txt950")} 10 ${getTranslation("Txt951")}`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    // Validate
    const newErrors: typeof errors = {};
    if (!firstName.trim()) {
      newErrors.firstName = getTranslation('Txt709') || 'Please enter your first name*';
    }
    if (!lastName.trim()) {
      newErrors.lastName = getTranslation('Txt711') || 'Please enter your last name*';
    }

    const phoneError = validatePhoneNumber(phoneNumber, countryId);
    if (phoneError) {
      newErrors.phoneNumber = phoneError;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSaving(true);

    try {
      const api = new ApiService(sessionId, token);
      await api.updateUserProfileDetails({
        userId: userId!,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: phoneNumber.replace(/\D/g, ''),
        country_id: countryId,
      });

      setSnackbar({
        open: true,
        message: getTranslation('Txt961'),
        severity: 'success',
      });

      // Stay on the page - no navigation needed
    } catch (error) {
      console.error('Failed to update profile:', error);
      setSnackbar({
        open: true,
        message: getTranslation('Txt962'),
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <Layout showBackButton={true}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-evenly",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Company Logo"
          sx={{
            height: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
            width: "100%",
            objectFit: "contain",
          }}
        />

        {/* Title */}
        <Typography sx={{ ...COMMON_MUI_STYLES.title1, mb: 3, mt: 2 }}>
          {getTranslation('Txt959')}
        </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { xs: "100%", sm: "70%", md: "35%" },
                justifyContent: { xs: "center", sm: "start" },
                mt: { xs: 0, sm: 0 },
                minHeight: { xs: "calc(100vh - 160px)", sm: "auto" },
                alignItems: "center",
                gap: { xs: 2, sm: 3 },
              }}
            >
              {/* First Name */}
              <TextField
                margin="normal"
                fullWidth
                id="first_name"
                placeholder={getTranslation('Txt708')}
                name="first_name"
                autoComplete="given-name"
                value={firstName}
                disabled={isLoading}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (errors.firstName) {
                    setErrors((prev) => ({ ...prev, firstName: undefined }));
                  }
                }}
                error={!!errors.firstName}
                helperText={errors.firstName}
                sx={{
                  ...COMMON_MUI_STYLES.textField,
                  "& .MuiOutlinedInput-root": {
                    ...COMMON_MUI_STYLES.textField["& .MuiOutlinedInput-root"],
                    "& fieldset": {
                      border: "1.5px solid var(--color-border)",
                    },
                    "&:hover fieldset": {
                      border: "1.5px solid var(--color-border-hover)",
                    },
                    "&.Mui-focused fieldset": {
                      border: "1.5px solid var(--color-info)",
                    },
                  },
                }}
                InputProps={{
                  style: isLoading 
                    ? { 
                        color: "var(--color-text-secondary)",
                        WebkitTextFillColor: "var(--color-text-secondary)",
                      } 
                    : undefined,
                }}
              />

              {/* Last Name */}
              <TextField
                margin="normal"
                fullWidth
                id="last_name"
                placeholder={getTranslation('Txt710')}
                name="last_name"
                autoComplete="family-name"
                value={lastName}
                disabled={isLoading}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (errors.lastName) {
                    setErrors((prev) => ({ ...prev, lastName: undefined }));
                  }
                }}
                error={!!errors.lastName}
                helperText={errors.lastName}
                sx={{
                  ...COMMON_MUI_STYLES.textField,
                  "& .MuiOutlinedInput-root": {
                    ...COMMON_MUI_STYLES.textField["& .MuiOutlinedInput-root"],
                    "& fieldset": {
                      border: "1.5px solid var(--color-border)",
                    },
                    "&:hover fieldset": {
                      border: "1.5px solid var(--color-border-hover)",
                    },
                    "&.Mui-focused fieldset": {
                      border: "1.5px solid var(--color-info)",
                    },
                  },
                }}
                InputProps={{
                  style: isLoading 
                    ? { 
                        color: "var(--color-text-secondary)",
                        WebkitTextFillColor: "var(--color-text-secondary)",
                      } 
                    : undefined,
                }}
              />

              {/* Phone Number */}
              <Box sx={{ width: "100%" }}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="phone_number"
                  placeholder={getTranslation("Txt953")}
                  name="phone_number"
                  autoComplete="tel"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setPhoneNumber(value);
                    if (value.trim()) {
                      const phoneErr = validatePhoneNumber(value, countryId);
                      if (phoneErr) {
                        setErrors((prev) => ({ ...prev, phoneNumber: phoneErr }));
                      } else if (errors.phoneNumber) {
                        setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
                      }
                    } else if (errors.phoneNumber) {
                      setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
                    }
                  }}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  inputProps={{
                    inputMode: "tel",
                    pattern: "[0-9]*",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          p: 0,
                          m: 0,
                          height: "100%",
                          maxHeight: "none",
                          marginRight: "10px",
                          alignSelf: "stretch",
                          "& .MuiInputAdornment-positionStart": {
                            marginRight: 0,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            minHeight: "56px",
                            padding: "10px !important",
                          }}
                        >
                          <Select
                            variant="standard"
                            disableUnderline
                            id="country_id"
                            name="country_id"
                            value={countryId}
                            onChange={(e) => {
                              setCountryId(e.target.value);
                              // Re-validate phone number when country changes
                              if (phoneNumber.trim()) {
                                const phoneError = validatePhoneNumber(phoneNumber, e.target.value);
                                if (phoneError) {
                                  setErrors((prev) => ({ ...prev, phoneNumber: phoneError }));
                                } else if (errors.phoneNumber) {
                                  setErrors((prev) => ({ ...prev, phoneNumber: undefined }));
                                }
                              }
                            }}
                            displayEmpty
                            renderValue={(selected) => {
                              // Check 1: No selection or countries not loaded
                              if (!selected || countries.length === 0) {
                                return getTranslation("Txt954");
                              }
                              
                              // Check 2: Find the country
                              const selectedCountry = countries.find(
                                c => c._id === selected 
                              );
                              
                              // Check 3: Country not found
                              if (!selectedCountry) {
                                return getTranslation("Txt954");
                              }
                              
                              // Check 4: Has dial code
                              if (selectedCountry.dial_code) {
                                const dialCode = selectedCountry.dial_code;
                                const flagCode = getFlagCodeFromDialCode(dialCode) || selectedCountry.country_code; // Use dial code mapping with fallback
                                return (
                                  <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    {flagCode && (
                                      <Box sx={{ position: 'relative', width: "1.2em", height: "1.2em", display: 'flex', alignItems: 'center' }}>
                                        {flagsLoading[flagCode] !== false && (
                                          <Skeleton
                                            variant="circular"
                                            width="1.2em"
                                            height="1.2em"
                                            sx={{ position: 'absolute', top: 0, left: 0 }}
                                          />
                                        )}
                                        <ReactCountryFlag
                                          countryCode={flagCode}
                                          svg
                                          style={{
                                            width: "1.2em",
                                            height: "1.2em",
                                            opacity: flagsLoading[flagCode] === false ? 1 : 0,
                                            transition: 'opacity 0.2s',
                                          }}
                                          title={flagCode}
                                          loading="lazy"
                                          onLoad={() => {
                                            setFlagsLoading(prev => ({ ...prev, [flagCode]: false }));
                                          }}
                                        />
                                      </Box>
                                    )}
                                    <span>+{dialCode}</span>
                                  </Box>
                                );
                              }
                              
                              // Fallback
                              return getTranslation("Txt954");
                            }}
                            sx={{
                              minWidth: { xs: "70px", sm: "85px" },
                              maxWidth: { xs: "90px", sm: "110px" },
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                                paddingTop: 0,
                                paddingBottom: 0,
                                fontWeight: 500,
                                cursor: "pointer",
                                caretColor: "transparent",
                                "&:focus": {
                                  caretColor: "transparent",
                                },
                              },
                              "& input": {
                                caretColor: "transparent",
                              },
                              "& svg": {
                                color: "var(--color-text-primary)",
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: { maxHeight: 300 },
                                sx: {
                                  "& .MuiList-root": {
                                    paddingLeft: 1,
                                    paddingRight: 1,
                                  },
                                  "& .MuiMenuItem-root": {
                                    paddingLeft: 1,
                                  },
                                },
                              },
                            }}
                          >
                            {countries.map((country, index) => (
                              <MenuItem
                                key={country._id || `country-${index}`}
                                value={country._id || ""}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    width: "100%",
                                    justifyContent: "flex-start",
                                    gap: 1,
                                  }}
                                >
                                  {(() => {
                                    const flagCode = getFlagCodeFromDialCode(country.dial_code) || country.country_code;
                                    return flagCode && (
                                      <Box sx={{ position: 'relative', width: "1.2em", height: "1.2em", display: 'flex', alignItems: 'center' }}>
                                        {flagsLoading[flagCode] !== false && (
                                          <Skeleton
                                            variant="circular"
                                            width="1.2em"
                                            height="1.2em"
                                            sx={{ position: 'absolute', top: 0, left: 0 }}
                                          />
                                        )}
                                        <ReactCountryFlag
                                          countryCode={flagCode}
                                          svg
                                          style={{
                                            width: "1.2em",
                                            height: "1.2em",
                                            opacity: flagsLoading[flagCode] === false ? 1 : 0,
                                            transition: 'opacity 0.2s',
                                          }}
                                          title={flagCode}
                                          loading="lazy"
                                          onLoad={() => {
                                            setFlagsLoading(prev => ({ ...prev, [flagCode]: false }));
                                          }}
                                        />
                                      </Box>
                                    );
                                  })()}
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {country.country_name || ""}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    +{country.dial_code}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.3125rem",
                      paddingLeft: "0 !important",
                      display: "flex",
                      alignItems: "stretch",
                      backgroundColor: "#fff",
                      "& .MuiInputAdornment-root": {
                        height: "100%",
                        alignSelf: "stretch",
                      },
                      "&:hover": {
                        backgroundColor: "#fff",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                      "& fieldset": {
                        border: "1.5px solid var(--color-border)",
                      },
                      "&:hover fieldset": {
                        border: "1.5px solid var(--color-border-dark)",
                      },
                      "&.Mui-focused fieldset": {
                        border: "1.5px solid #1976d2",
                      },
                    },
                    "& .MuiInputBase-input": {
                      borderRadius: "0 !important",
                      backgroundColor: "#fff !important",
                      "&:focus": {
                        backgroundColor: "#fff !important",
                      },
                    },
                  }}
                />
              </Box>

              {/* Save Button */}
              <Button
                type="button"
                variant="contained"
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  ...COMMON_MUI_STYLES.primaryCtaButton1,
                  width: { xs: "100%", sm: "60%", md: "auto" },
                  mt: 3,
                  mb: 2,
                  "& > *:not(.loader-overlay)": {
                    opacity: isSaving ? 0 : 1,
                    transition: "opacity 0.2s ease-in-out",
                  },
                }}
              >
                <span>{getTranslation("Txt960")}</span>
                {isSaving && (
                  <CircularProgress
                    size={20}
                    className="loader-overlay"
                    sx={{
                      color: "var(--color-text-inverse)",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-10px",
                      marginLeft: "-10px",
                    }}
                  />
                )}
              </Button>
            </Box>
          )}
      </Box>
    </Layout>
  );
};

export default EditProfilePage;

